import { PartnerDbModel } from '@models/partner-db.model';
import { StoreDbModel } from './store-db.model';

export class WidgetConfModel {
  id: number;
  widget_uuid: string;
  locale: string;
  lang: string;
  position: string;
  header_background_color: string; // this color we are going to use for background color of selected card
  header_font_color: string;
  button_background_color: string;
  button_font_color: string;
  title: string;
  description: string;
  logo_url: string;
  launcher_icon: number;
  type: number;
  trigger: number;
  whitelisted_domains: string[];
  partner_id: number;
  right_padding: number;
  left_padding: number;
  bottom_padding: number;
  context: WidgetContext;
  trigger_message: string;
  lead_intro_text: string;
  lead_outro_text: string;
  partner: PartnerDbModel;
  supported_widget_languages: string[] = [];
  is_multi_language: number;
  start_text: string;
  end_text: string;
  end_of_process_message: string;
  _translations: {
    [key: string]: WidgetConfTranslationsModel;
  };
}

class WidgetConfTranslationsModel {
  end_of_process_message?: string;
  end_text?: string;
  locale: string;
  start_text?: string;
  title?: string;
}

export class WidgetContext {
  locale: string;
  lang: string;
  worker_email: string;
  worker_id: number;
  worker_ids: number[] = [];
  worker_uuid: string;
  appointment_service_ids: number[] = [];
  store_zip: string;
  store_id: number;
  store?: StoreDbModel;
  store_uuids: string[] = [];
  date: string;
  is_internal: boolean;
  booking_questions: number[] = [];
  // [BKB] Opt-in: auto-load the booking questions configured on the customer's selected
  // appointment-service(s) (mirrors the classic widget) in addition to `booking_questions` above.
  // NO initializer — absence must serialize as "key not present" (cleanContext() drops
  // undefined/null/''/[]), so an existing config without this flag stays byte-for-byte unchanged.
  // See docs/partner-context-features.md and BookingService.isAutoLoadBookingQuestionsEnabled().
  auto_load_booking_questions?: boolean;
  // [BKB] When auto_load_booking_questions is ON, choose where the loaded questions render:
  // 'dedicated_step' (default/absent) keeps them in their own post-slots step (unchanged
  // behaviour); 'personal_form' embeds them inside the personal-details form instead, alongside
  // context.customer's manually-configured fields — no separate step. NO initializer — absence
  // must serialize as "key not present", same byte-for-byte-when-absent contract as
  // auto_load_booking_questions above. See docs/partner-context-features.md and
  // BookingService.getAutoLoadBookingQuestionsPlacement().
  auto_load_booking_questions_placement?: 'dedicated_step' | 'personal_form';
  // [BKB] When auto_load_booking_questions is ON, choose where the loaded questions sit relative
  // to the manually-configured ones: 'bottom' (default/absent) appends them, unchanged behaviour;
  // 'top' prepends them at the FRONT of the list — for placement 'personal_form' that means above
  // context.customer's contact fields too, since the loaded set is only known at runtime and can
  // therefore not be ordered by hand in the setup page. NO initializer — absence must serialize as
  // "key not present", same byte-for-byte-when-absent contract as the two settings above. See
  // docs/partner-context-features.md and BookingService.getAutoLoadBookingQuestionsPosition().
  auto_load_booking_questions_position?: 'top' | 'bottom';
  meeting_providers: number[] = [];
  // Holds standard customer-field keys (strings) AND, for BOOKING, inline booking-question ids
  // (numbers) at runtime; kept typed `string[]` because several consumers assign it to `string[]`
  // fields — reads widen via `(string|number)[]` / `typeof` guards where the numbers matter.
  customer: string[] = [];
  customer_required: string[] = [];
  customer_readonly: string[] = [];
  customer_prefill: { [fieldName: string]: string } = {};
  // Per-widget required for booking questions (by id). The personal form marks a customer-form
  // question required when its id is present here — additive over the question's own `required`
  // flag (see personal-form.component.ts). Written by the dashboard customer step.
  required_booking_questions: number[] = [];
  booking_questions_prefill: { [questionId: string]: unknown } = {};
  customer_type_id: number;
  show_questions_before_booking_process: boolean;
  steps: number[] = [];
  ask_for_customer_notification_preference: boolean;
  show_meeting_type_picker: boolean;
  show_additional_services_provided_by_meeting_providers: number[] = [];
  show_appointment_services_list: boolean;
  show_worker_avatar: boolean;
  auto_skip_steps: number;
  // Clears the widget-owned localStorage keys on load, so the funnel starts fresh instead of
  // rehydrating the last visitor's answers. Same key set as the `refresh` QUERY PARAM, which
  // still wins whenever PRESENT (`?refresh=0` vetoes this). NOT identical to the param: applied
  // at most ONCE per load, so a mid-flow re-config cannot wipe an in-progress funnel — the param
  // has no latch and re-fires on every mount, so it stays the tool for "clear on every re-config".
  // NO initializer: absence must serialize as absent (cleanContext() drops undefined/null/''/[]).
  // See docs/partner-context-features.md and SwRouteService.applyContextRefresh().
  refresh?: number | string;
  ask_customer_city: 0 | 1 = 0;
  ask_customer_zip: 0 | 1 = 0;
  workers_view: 'card' | 'dropdown';
  utm_source: string;
  utm_content: string;
  utm_term: string;
  utm_medium: string;
  utm_campaign: string;
  utm_id: string;
  category: number[];
  smart_widget_birthday_minimum_age_18: boolean;
  widget_vat_type: string;
  coupon: string;

  // [Sanitas]
  enable_one_page_ui: boolean;

  // [Helsana] Start
  saluation_display_type: "selection" | "radio";
  filter_meetingtype_services_based_on_store_id: boolean;
  show_worker_preview_on_date_selection_step: boolean;
  only_allow_store_selection_for_direct_sales: boolean;
  show_store_selection_for_customer_meeting_type: boolean;
  
  // They want to automatically step forward on option selection for the question, worker, and service steps
  auto_step_forward_on_select: boolean;
  // Master on/off switch for the language→service mapping below.
  enable_language_service_map: boolean;
  // Maps a language family (e.g. "de", "en", "fr") to a single appointment service id.
  // When enabled, the widget resolves a locale (browser language → URL lang/locale →
  // first configured), auto-selects the mapped service and skips the service-selection step.
  // Keyed by language family so every regional variant lands on the same entry (de_DE/de_AT → "de",
  // en_GB/en_AU/en_CA → "en"). A full-locale key ("de_CH", "en_US") is still honoured for older configs.
  language_service_map: { [language: string]: number };
  // [Helsana] End

  // [NKB]
  select_any_worker_for_selected_meeting_type: number[] = [];

  // [KAPO / generic] Booking steps removed from the flow entirely (render AND navigation),
  // driven by widget context. Values are BOOKING_PAGE_VIEWS keys, e.g. MEETING_TYPE_VIEW /
  // STORE_VIEW / SERVICES_VIEW / WORKERS_VIEW. Each hidden step must resolve a forced value
  // (meeting type from context.meeting_providers[0] when exactly one is configured, store from
  // context.store_id, service from the answer's supported_appointment_service_ids narrowed to one,
  // worker = 0/Anyone) or it falls back to rendering normally — never an invisible dead-end.
  // See docs/partner-context-features.md and BookingService.isStepHidden().
  hidden_booking_steps?: string[];

  // Per-smart-widget redirect to external success page.
  // Resolved per-widget FIRST; when a field is unset here the resolver falls back to the
  // partner-level (global) setting — see BookingService redirect getters.
  is_redirect_after_booking_enabled?: number;
  successful_booking_redirect_url?: string;
  redirect_after_booking_action?: string;

  // Success-page actions (generic, dashboard-exposed — booking-flow step toggles).
  // Both are optional with NO initializer — absence must serialize as "key not
  // present" (the dashboard's cleanContext() drops undefined/null/''/[]), so an existing
  // config without these flags re-serializes byte-for-byte unchanged. Both are opt-in
  // (absent = hidden). See docs/partner-context-features.md and the BookingService
  // success-page gates.
  show_appointment_link?: boolean;
  show_ics_download?: boolean;
}

export interface CurrentViewData {
  view: string;
  isUserInteracted: boolean;
}
